import React, { memo, useCallback, useEffect, useState } from 'react'
import { Button, Modal, Spin, message, Alert } from 'antd'
import fetchApi from '../../../../../../lib/utils/fetch-api'
import './style.css'

interface ITemplate {
  id: number
  name: string
  file_name?: string
  message?: string
}

interface HrModalProps {
  id: string | number
  onClose?: ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined
  template?: ITemplate
}

interface IMessage {
  name: string
  text: string
}

function htmlToPlainText(html: string): string {
  const normalizedHtml = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')

  const div = document.createElement('div')
  div.innerHTML = normalizedHtml

  return (div.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .trim()
}

// MAX/VK и похожие мессенджеры при отправке схлопывают пустые строки из буфера.
// Невидимый символ на «пустой» строке сохраняет перенос при ручном вводе.
function prepareClipboardTextForMessenger(text: string): string {
  return text
    .split('\n')
    .map(line => (/^\s*$/.test(line) ? '\u200b' : line))
    .join('\n')
}

const MessageCopyModal: React.FC<HrModalProps> = memo((props: HrModalProps) => {
  const { onClose, template, id } = props
  const [readyMessage, setReadyMessage] = useState<undefined | IMessage>(undefined)
  const [error, setError] = useState<undefined | string>(undefined)
  const [visible, setVisible] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)
  const [copied, setCopied] = useState(false)

  const fetchData = useCallback(
    async (id, card_id) => {
      await fetchApi(`/api/template_message/${id}/${card_id}`)
        .then(response => {
          if (response?.error) {
            setError(response.error)
          }
          if (response) {
            setReadyMessage(response as IMessage)
          }
        })
        .catch(e => {
          const message = e?.message || 'Не определена'
          setError('Ошибка: ' + message)
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [setReadyMessage, setError, setLoading],
  )

  useEffect(() => {
    return () => {
      setError(undefined)
    }
  }, [])

  useEffect(() => {
    if (template?.id) {
      fetchData(template.id, id)
    }
  }, [template, id])

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    setVisible(false)
    setTimeout(() => {
      onClose?.(e)
    }, 500)
  }

  const handleCopy = useCallback(async () => {
    if (readyMessage?.text) {
      try {
        const plainText = prepareClipboardTextForMessenger(htmlToPlainText(readyMessage.text))
        await navigator.clipboard.writeText(plainText)
        setCopied(true)
        message.success('Текст скопирован в буфер обмена')
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        message.error('Не удалось скопировать текст')
      }
    }
  }, [readyMessage])

  if (!template) {
    return null
  }

  return (
    <Modal
      title={(readyMessage && readyMessage?.name) || 'Загрузка ...'}
      centered={true}
      open={visible}
      closable={true}
      destroyOnHidden={true}
      onCancel={handleClose}
      footer={null}
      width={560}
      className="message-copy-modal"
    >
      {loading ? (
        <div className="message-copy-modal__loader">
          <Spin spinning />
        </div>
      ) : null}
      {error ? <Alert type="error" description={error} showIcon /> : null}
      {readyMessage ? (
        <div className="message-copy-modal__content">
          <div
            className="formatted-message"
            dangerouslySetInnerHTML={{ __html: readyMessage.text }}
          />
          <div className="message-copy-modal__actions">
            <Button size="small" type={copied ? 'primary' : 'default'} onClick={handleCopy}>
              {copied ? 'Скопировано!' : 'Скопировать'}
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  )
})

MessageCopyModal.displayName = 'MessageCopyModal'

export default MessageCopyModal
